// Base controls of the ECONIC look (APPLE-SPEC §5.6, §7 10.11–10.13). WP-THEME.
// Rules built in: buttons 32 (regular) / 28 (compact) / 36 (large) / 44 (phone block), radius 6 (8 for the block), 13 px 500; primary =
// navy fill + white, secondary = the 12 % fill + label, plain = navy text, AI (`teal`) = secondary with a teal glyph, danger = fill +
// red-ink; one primary per view and the default action LAST in its group (ButtonGroup); no gold on controls (the legacy "gold" variant
// draws as primary); icon buttons 28 × 28 with aria-label and title and the count badge on the glyph's shoulder (never over it, in LTR and
// RTL); segmented controls instead of pill tabs, counts as plain text (red-ink 600 only when due); the 32 × 19 switch, navy when on;
// inputs 32 with an inset 1 px edge and the teal focus ring; disabled = .45 opacity + aria-disabled.
//
// Names:  Button · ButtonGroup · IconButton · SegmentedControl · Switch · SearchField · SearchButton · TextField · CountBadge
// First-pass names (kept, same classes): EconicButton · econicButtonClass · Segmented · Toggle
import {
  Children,
  forwardRef,
  isValidElement,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cx } from "./lang";

export type EconicButtonVariant = "primary" | "secondary" | "plain" | "ghost" | "teal" | "danger" | "gold";
/** `sm` = compact 28 px (headers, cards), `md` = regular 32 px, `lg` = 36 px, `block` = the 44 px full-width phone button (15/600). */
export type EconicButtonSize = "sm" | "md" | "lg" | "block";

export interface EconicButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: EconicButtonVariant;
  size?: EconicButtonSize;
  /** A leading icon (16 px, 14 in compact); with no children the button is icon-only and must carry `aria-label`. */
  icon?: ReactNode;
  /** A trailing icon (a chevron for menus) or a keyboard hint. */
  trailing?: ReactNode;
}

export const EconicButton = forwardRef<HTMLButtonElement, EconicButtonProps>(function EconicButton(
  { variant = "secondary", size = "md", icon, trailing, className, children, type = "button", disabled, title, ...rest },
  ref,
) {
  const iconOnly = children == null || children === false;
  return (
    <button
      ref={ref}
      type={type}
      className={cx("ec-btn", variant, size !== "md" && size, iconOnly && "icon", className)}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      title={title ?? (iconOnly ? rest["aria-label"] : undefined)}
      {...rest}
    >
      {icon}
      {children}
      {trailing}
    </button>
  );
});

/** The §7 button: `primary` (navy), `secondary` (the fill, the default) or `plain` (navy text); `ghost`, `teal` (AI), `danger` as well. */
export const Button = EconicButton;
export type ButtonProps = EconicButtonProps;

/** Class names for a link or another element that should look like a button (e.g. a router <Link>). */
export function econicButtonClass(variant: EconicButtonVariant = "secondary", size: EconicButtonSize = "md", extra?: string): string {
  return cx("ec-btn", variant, size !== "md" && size, extra);
}
export const buttonClass = econicButtonClass;

export interface ButtonGroupProps extends HTMLAttributes<HTMLDivElement> {
  /** Start-aligned instead of pushed to the end of its row. */
  start?: boolean;
}

/**
 * An action group (APPLE-SPEC §0.1 rule 7, §6.3): one row that wraps as a unit, pushed to the end of its line, with the default action
 * LAST. Children are reordered so that a `variant="primary"` button (or an element with `data-default`) comes last, whatever order the
 * caller wrote them in.
 */
export function ButtonGroup({ start = false, className, children, ...rest }: ButtonGroupProps) {
  const items = Children.toArray(children);
  const isDefault = (c: ReactNode) => {
    if (!isValidElement(c)) return false;
    const p = c.props as Record<string, unknown>;
    return p.variant === "primary" || p["data-default"] != null;
  };
  const ordered = [...items.filter((c) => !isDefault(c)), ...items.filter(isDefault)];
  return (
    <div role="group" className={cx("ec-btngroup", start && "start", className)} {...rest}>
      {ordered}
    </div>
  );
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** The glyph (lucide 16 px, stroke 1.5) or a short text glyph ("ع", "EN"). */
  icon: ReactNode;
  /** Required accessible name; also the tooltip. */
  label: string;
  /** 28 (default, the toolbar), 26 (the inspector header) or 32. */
  size?: "sm" | "md" | "lg";
  /** A count shown as the 16 px count badge (only when > 0; "9+" above nine): red (--badge), or navy for suggestions. */
  badge?: number;
  badgeTone?: "red" | "navy";
  /** What the badge means, for assistive technology ("3 decisions are waiting for you"). */
  badgeLabel?: string;
}

/** The 28 × 28 toolbar glyph button (§5.6): transparent, hover fill, glyph in the secondary label colour. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = "md", badge, badgeTone = "red", badgeLabel, className, type = "button", title, disabled, ...rest },
  ref,
) {
  const shown = badge != null && badge > 0 ? (badge > 9 ? "9+" : String(badge)) : null;
  return (
    <button
      ref={ref}
      type={type}
      className={cx("ec-iconbtn", size !== "md" && size, className)}
      aria-label={label}
      title={title ?? label}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      {...rest}
    >
      {icon}
      {shown && (
        <span className={cx("ec-badge-dot", badgeTone === "navy" && "navy")} aria-hidden={badgeLabel ? true : undefined}>
          {shown}
        </span>
      )}
      {shown && badgeLabel && <span className="sr-only">{badgeLabel}</span>}
    </button>
  );
});

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Accessible name when the label is an icon. */
  ariaLabel?: string;
  /** A plain count after the label (label-2-on; red-ink 600 only when `due`). */
  count?: number;
  due?: boolean;
  disabled?: boolean;
  /** A 0.5 × 14 px rule before this segment (the "Knowledge Bank │ modules" divide of the Intelligence row). */
  divideBefore?: boolean;
}

export interface SegmentedProps<T extends string> extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** The group's accessible name ("Scope", "View"). */
  label: string;
  /** 28 px track (default), 32 px (lg) or the 25 px composer track (sm). */
  size?: "sm" | "md" | "lg";
  /** Stretch the segments to the container's width. */
  block?: boolean;
}

/** The segmented control (§7, 10.11): a 6 px fill track with a 2 px inset, the selected segment white (dark #505054) with --shadow-control. */
export function SegmentedControl<T extends string>({ options, value, onChange, label, size = "md", block = false, className, ...rest }: SegmentedProps<T>) {
  return (
    <div role="group" aria-label={label} className={cx("ec-seg", size !== "md" && size, block && "block", className)} {...rest}>
      {options.map((o) => [
        o.divideBefore ? <span key={`${o.value}-div`} className="div" aria-hidden /> : null,
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          aria-label={o.ariaLabel}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count != null && <span className={cx("n", o.due && "due")}>{o.count}</span>}
        </button>,
      ])}
    </div>
  );
}
/** First-pass name of SegmentedControl. */
export const Segmented = SegmentedControl;

export interface ToggleProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Accessible name; a visible label next to the switch may be referenced with aria-labelledby instead. */
  label?: string;
  /** 32 × 19 (default) or 38 × 22. */
  size?: "md" | "lg";
}

/** The switch (32 × 19; on = navy, never green). */
export function Switch({ checked, onChange, label, size = "md", className, ...rest }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={cx("ec-switch", size === "lg" && "lg", className)}
      onClick={() => onChange(!checked)}
      {...rest}
    />
  );
}
/** First-pass name of Switch. */
export const Toggle = Switch;
export type SwitchProps = ToggleProps;

export interface SearchFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** The magnifier glyph (lucide `Search`, 14 px); the field draws none of its own so @ncp/ui keeps no icon dependency here. */
  icon?: ReactNode;
  /** A keyboard hint at the end ("⌘K"); decorative, hidden from assistive technology. Hidden while the field holds a query. */
  shortcut?: string;
  /** Accessible name (defaults to the placeholder). */
  label?: string;
  /** Width in px (220 in the toolbar). */
  width?: number | string;
  /** Shows the 16 px round clear button while the field holds text; called when it is pressed. */
  onClear?: () => void;
  /** The clear button's glyph (lucide `X`) and accessible name. */
  clearIcon?: ReactNode;
  clearLabel?: string;
}

/** The search field (§5.5): 28 px, the 12 % fill, magnifier and placeholder in label-2-on, a ⌘K hint; focused it turns white with a teal
 *  glow and the hint gives way to the clear button. For a button that opens a palette, use SearchButton. */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  { icon, shortcut, label, width, className, style, placeholder = "Search", onClear, clearIcon, clearLabel = "Clear", value, ...rest },
  ref,
) {
  const filled = value != null && String(value).length > 0;
  return (
    <label className={cx("ec-search", className)} style={{ width, ...style }}>
      {icon}
      <input ref={ref} type="search" placeholder={placeholder} aria-label={label ?? placeholder} value={value} {...rest} />
      {filled && onClear ? (
        <button type="button" className="clear" aria-label={clearLabel} title={clearLabel} onClick={onClear}>
          {clearIcon ?? "×"}
        </button>
      ) : shortcut ? (
        <kbd className="ec-kbd" aria-hidden>
          {shortcut}
        </kbd>
      ) : null}
    </label>
  );
});

export interface SearchButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon?: ReactNode;
  placeholder?: string;
  shortcut?: string;
  width?: number | string;
}

/** The toolbar's search field when it opens a popover or palette: looks like SearchField, behaves as a button. */
export const SearchButton = forwardRef<HTMLButtonElement, SearchButtonProps>(function SearchButton(
  { icon, placeholder = "Search", shortcut, width, className, style, type = "button", ...rest },
  ref,
) {
  return (
    <button ref={ref} type={type} className={cx("ec-search", className)} style={{ width, ...style }} {...rest}>
      {icon}
      <span className="ph">{placeholder}</span>
      {shortcut && (
        <kbd className="ec-kbd" aria-hidden>
          {shortcut}
        </kbd>
      )}
    </button>
  );
});

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** 28 (compact), 32 (regular, default; the sign-in card too) or 36. */
  size?: "sm" | "md" | "lg";
  /** A leading glyph (14 px; 16 at lg). */
  icon?: ReactNode;
  /** Content at the end (a reveal button, a unit). */
  end?: ReactNode;
  invalid?: boolean;
}

/** A text input in the field frame (§7, 10.12): 32 px, radius 6, an inset 1 px edge, the teal focus ring (3.5 px glow). */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { size = "md", icon, end, invalid, className, style, ...rest },
  ref,
) {
  return (
    <span className={cx("ec-input", size !== "md" && size, invalid && "err", className)} style={style}>
      {icon}
      <input ref={ref} aria-invalid={invalid || undefined} {...rest} />
      {end}
    </span>
  );
});

/** A count (§7, 10.10 `.count-b`): a plain 13/600 tabular number, red-ink when an action is due; never a capsule. */
export interface CountBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  count: number;
  /** Red-ink only when action is due (an overdue approval, a crossed line). */
  due?: boolean;
  /** 500 instead of 600 (segmented rows, the module rail). */
  muted?: boolean;
  /** A 16 px navy count badge (rare: a count that is itself the call to action). */
  strong?: boolean;
  size?: "sm" | "md";
  /** What the count means, for assistive technology ("7 decisions are waiting for you"). */
  label?: string;
}

export function CountBadge({ count, due = false, muted = false, strong = false, size = "md", label, className, ...rest }: CountBadgeProps) {
  return (
    <span className={cx("ec-count", due && "due", muted && !due && "muted", strong && !due && "strong", size === "sm" && "sm", className)} {...rest}>
      <span aria-hidden={label ? true : undefined}>{count}</span>
      {label && <span className="sr-only">{label}</span>}
    </span>
  );
}
